<template>
    <div class="navbar-container">
        <div class="navbar-content">
            <div
                class="logo-wrapper"
                @click="pageNameNavigation('Home')"
                @keydown.enter="pageNameNavigation('Home')"
            >
                <primevue-button class="mobile-menu-btn" @click="openSideNav" v-tooltip.bottom="'Menu'" v-if="currentRouteHasSidebar">
                    <font-awesome-icon :icon="['fas', uiStore.showNavSidebar ? 'chevron-left' : 'chevron-right']" />
                </primevue-button>
                <span id="header-sidebar-visibility-toggle" />
                <primevue-button class="logo-content" @click="pageNameNavigation('Home')">
                    <spark-logo class="logo spark" />
                    <span class="logo by-delta">by <delta-logo class="delta-logo" /><sup>&#8482;</sup></span>
                </primevue-button>
                <primevue-tag v-if="!isProd" v-tooltip="'This is a test site, to visit the live production site go to spark.lcp.com'" class="test-site-tag">TEST</primevue-tag>
            </div>
            <div class="header-navbar-center">
                <div class="header-navbar-center-content" :class="{ 'show-page-links': showPageLinks }">
                    <template v-if="route.name === 'Home'">
                        <template v-if="isMobileView">
                            <accordion class="mobile-navbar-accordion">
                                <template #expandicon>
                                    <font-awesome-icon :icon="['fas', 'chevron-down']" />
                                </template>
                                <template #collapseicon>
                                    <font-awesome-icon :icon="['fas', 'chevron-up']" />
                                </template>
                                <accordion-tab
                                    v-for="section in Object.values(sectionRoutes)"
                                    :key="section.name"
                                >
                                    <template #header>
                                        <primevue-button
                                            text
                                            class="navbar-icon-button accordion-header-button"
                                        >
                                            {{ section.meta ? section.meta.headName : section.name }}
                                        </primevue-button>
                                    </template>
                                    <div v-if="getDropdownChildren(section).length" class="accordion-content-wrapper">
                                        <div class="navbar-dropdown-routes">
                                            <div v-for="child in getDropdownChildren(section)" :key="child.name">
                                                <primevue-button
                                                    text
                                                    class="navbar-dropdown-button"
                                                    :class="child.meta?.showChildrenInNavDropdown ? 'has-grandchildren' : ''"
                                                    @click="pageNameNavigation(child.name)"
                                                >
                                                    <span class="text">
                                                        {{ child.meta ? (child.meta.displayTitle || child.meta.headName) : child.name }}
                                                    </span>
                                                </primevue-button>
                                                <div v-if="child.meta?.showChildrenInNavDropdown">
                                                    <primevue-button
                                                        v-for="grandchild in getDropdownChildren(child)"
                                                        :key="grandchild.name"
                                                        text
                                                        class="navbar-dropdown-button grandchild-button"
                                                        @click="pageNameNavigation(grandchild.name)"
                                                    >
                                                        <span class="text">
                                                            {{ grandchild.meta ? (grandchild.meta.displayTitle || grandchild.meta.headName) : grandchild.name }}
                                                        </span>
                                                    </primevue-button>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="navbar-dropdown-content">
                                            <div class="navbar-dropdown-image">
                                                <img :src="sectionRouteInformation[section.name as SectionRouteNames].img" alt="" />
                                            </div>
                                            <p class="navbar-dropdown-text">{{ sectionRouteInformation[section.name as SectionRouteNames].info }}</p>
                                        </div>
                                    </div>
                                    <div v-else class="accordion-content-wrapper">
                                        <p class="no-children-message">No sub-sections available</p>
                                    </div>
                                </accordion-tab>
                            </accordion>
                        </template>
                        <template v-else>
                            <div
                                v-for="section in Object.values(sectionRoutes)"
                                :key="section.name"
                                class="navbar-dropdown-button-wrapper"
                                @mouseenter="routeHoverEnter(section)"
                                @focusin="routeHoverEnter(section)"
                                @mouseleave="routeHoverLeave"
                                @focusout="routeHoverLeave"
                            >
                                <primevue-button
                                    text
                                    class="navbar-icon-button"
                                    :class="{
                                        hovered: hoveredRoute && section.name === hoveredRoute.name,
                                        radius: !getDropdownChildren(section).length,
                                    }"
                                    @click="pageNameNavigation(section.name)"
                                >
                                    {{ section.meta ? section.meta.headName : section.name }}
                                    <transition name="fade">
                                        <font-awesome-icon
                                            v-if="hoveredRoute && section.name === hoveredRoute.name && getDropdownChildren(hoveredRoute).length"
                                            :icon="['fas', 'chevron-down']"
                                            class="dropdown-icon"
                                        />
                                    </transition>
                                </primevue-button>
                                <transition name="fade-delay" mode="out-in">
                                    <div
                                        v-if="hoveredRoute && section.name === hoveredRoute.name && getDropdownChildren(hoveredRoute).length"
                                        class="navbar-dropdown-wrapper"
                                    >
                                        <div class="navbar-dropdown-routes">
                                            <div v-for="child in getDropdownChildren(hoveredRoute)" :key="child.name">
                                                <primevue-button
                                                    @mouseenter="dropdownHoverEnter(child)"
                                                    @focusin="dropdownHoverEnter(child)"
                                                    @mouseleave="dropdownHoverLeave()"
                                                    @focusout="dropdownHoverLeave()"
                                                    text
                                                    class="navbar-dropdown-button"
                                                    :class="child.meta?.showChildrenInNavDropdown ? 'has-grandchildren' : ''"
                                                    @click="pageNameNavigation(child.name)"
                                                >
                                                    <span class="text">
                                                        <span v-if="child.meta?.headName === 'Enact'" class="dropdown-enact-brand">
                                                            <img
                                                                class="dropdown-enact-bulb"
                                                                :src="uiStore.theme === 'dark' ? '/images/logos/enact-bulb.svg' : '/images/logos/enact-bulb-light.svg'"
                                                                alt=""
                                                            />
                                                            Enact
                                                        </span>
                                                        <template v-else>
                                                            {{ child.meta ? (child.meta.displayTitle || child.meta.headName) : child.name }}
                                                        </template>
                                                        <transition name="fade">
                                                            <font-awesome-icon
                                                                v-if="hoveredDropdown && hoveredDropdown.name === child.name"
                                                                :icon="['fas', 'arrow-right']"
                                                                class="dropdown-icon"
                                                            />
                                                        </transition>
                                                    </span>
                                                </primevue-button>
                                                <div v-if="child.meta?.showChildrenInNavDropdown" class="navbar-dropdown-routes">
                                                    <primevue-button
                                                        v-for="grandchild in getDropdownChildren(child)"
                                                        :key="grandchild.name"
                                                        @mouseenter="dropdownHoverEnter(grandchild)"
                                                        @focusin="dropdownHoverEnter(grandchild)"
                                                        @mouseleave="dropdownHoverLeave()"
                                                        @focusout="dropdownHoverLeave()"
                                                        text
                                                        class="navbar-dropdown-button grandchild-button"
                                                        @click="pageNameNavigation(grandchild.name)"
                                                    >
                                                        <span class="text">
                                                            {{ grandchild.meta ? (grandchild.meta.displayTitle || grandchild.meta.headName) : grandchild.name }}
                                                            <transition name="fade">
                                                                <font-awesome-icon
                                                                    v-if="hoveredDropdown && hoveredDropdown.name === grandchild.name"
                                                                    :icon="['fas', 'arrow-right']"
                                                                    class="dropdown-icon"
                                                                />
                                                            </transition>
                                                        </span>
                                                    </primevue-button>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="navbar-dropdown-content">
                                            <div class="navbar-dropdown-image">
                                                <img :src="sectionRouteInformation[hoveredRoute.name as SectionRouteNames].img" alt="" />
                                            </div>
                                            <p class="navbar-dropdown-text">{{ sectionRouteInformation[hoveredRoute.name as SectionRouteNames].info }}</p>
                                        </div>
                                    </div>
                                </transition>
                            </div>
                        </template>
                    </template>
                    <div v-else-if="currentSection" class="stacked-routes-wrapper">
                        <div class="breadcrumb-container">
                            <primevue-button text class="breadcrumb-button" @click="pageNameNavigation('Home')">
                                <font-awesome-icon :icon="['fad', 'house']" />
                            </primevue-button>
                            <font-awesome-icon class="breadcrumb-separator" :icon="['fas', 'chevron-right']" />
                            <div class="breadcrumb-selected">
                                {{ currentSection.meta ? currentSection.meta.headName : currentSection.name }}
                            </div>
                        </div>
                        <div v-if="currentSection.children && isCurrentRouteASection" class="navbar-routes-wrapper">
                            <div class="navbar-routes-content">
                                <template v-for="child in currentSection.children" :key="child.name">
                                    <div class="navbar-routes-button-holder" v-if="child.meta?.showChildrenInNavbar">
                                        <div
                                            class="navbar-routes-button-header"
                                            :class="{ 'enact-brand': child.meta?.headName === 'Enact' }"
                                        >
                                            <img
                                                v-if="child.meta?.headName === 'Enact'"
                                                class="enact-logo"
                                                :src="uiStore.theme === 'dark' ? '/images/logos/enact.svg' : '/images/logos/enact-light.svg'"
                                                alt="Enact"
                                            />
                                            <template v-else>
                                                {{ child.meta ? (child.meta.displayTitle || child.meta.headName) : child.name }}
                                                <sup>&#8482;</sup>
                                            </template>
                                        </div>
                                        <primevue-button
                                            v-for="childChild in child.children?.filter(el => el.path)"
                                            :key="childChild.name"
                                            text
                                            class="navbar-routes-button"
                                            :class="{ selected: checkLinkSelected(childChild) }"
                                            @click="pageNameNavigation(childChild.name)"
                                        >
                                            {{ childChild.meta ? (childChild.meta.displayTitle || childChild.meta.headName) : childChild.name }}
                                        </primevue-button>
                                    </div>
                                    <primevue-button
                                        v-else
                                        text
                                        class="navbar-routes-button"
                                        :class="{ selected: checkLinkSelected(child) }"
                                        @click="pageNameNavigation(child.name)"
                                    >
                                        {{ child.meta ? (child.meta.displayTitle || child.meta.headName) : child.name }}
                                    </primevue-button>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="navbar-end">
                <div v-if="!isMobileView" class="nav-actions flex-row-with-gap">
                    <search-modal :is-embedded="false" />
                    <primevue-button
                        v-if="canViewCommunity"
                        text
                        class="navbar-icon feature-button"
                        v-tooltip.bottom="'Notifications'"
                        @click="openPageCommunityModal"
                    >
                        <icon-with-count-badge :icon="['fas', 'bell']" :count="headerAttentionCount" aria-label="Notifications count" />
                    </primevue-button>
                    <primevue-button text class="navbar-icon" v-tooltip.bottom="'Feedback'" @click="toggleFeedbackMenu" v-if="authState.loggedIn">
                        <font-awesome-icon :icon="['fas', 'comment-dots']" />
                    </primevue-button>
                    <primevue-button text class="navbar-icon" v-tooltip.bottom="'Tutorial'" @click="toggleTutorialMenu">
                        <font-awesome-icon :icon="['fas', 'graduation-cap']" />
                    </primevue-button>
                    <primevue-button
                        text
                        class="navbar-icon"
                        @click="toggleSettingsDrawer"
                        v-tooltip.bottom="'Settings'"
                    >
                        <font-awesome-icon :icon="['fad', 'gears']" />
                    </primevue-button>
                </div>
                <primevue-button v-if="!isMobileView && authState.loggedIn" class="navbar-icon" text @click="toggleProfile" v-tooltip.bottom="'User menu'">
                    <font-awesome-icon :icon="['fas', 'user']" />
                </primevue-button>
                <div v-else-if="!isMobileView" class="logged-out-buttons">
                    <primevue-button @click="setShowContactForm(true)" class="plain-btn no-wrap" aria-label="Contact us">
                        {{ isMobileView ? '' : 'Contact us' }}
                        <font-awesome-icon :icon="['fas', 'envelope']" />
                    </primevue-button>
                    <primevue-button @click="triggerLogin" class="login-btn" aria-label="Login">
                        {{ isMobileView ? '' : 'Login' }}
                        <font-awesome-icon :icon="['fas', 'fa-right-to-bracket']" />
                    </primevue-button>
                </div>
                <primevue-button
                    v-if="isMobileView"
                    class="mobile-actions-btn"
                    text
                    @click="toggleMobileActions"
                    v-tooltip.bottom="'Actions'"
                    aria-label="Actions"
                >
                    <font-awesome-icon :icon="['fas', 'ellipsis']" />
                </primevue-button>
                <primevue-button class="mobile-nav-burger" text @click="showPageLinks = !showPageLinks" v-tooltip.bottom="'Menu'" aria-label="Menu">
                    <font-awesome-icon :icon="['fas', showPageLinks ? 'xmark' : 'bars']" />
                </primevue-button>
            </div>
        </div>
        <popover ref="mobileActions">
            <div class="mobile-actions-overlay">
                <div class="mobile-actions-item search" tabindex="0">
                    <search-modal :is-embedded="false" />
                </div>
                <div class="mobile-actions-separator" />
                <primevue-button v-if="canViewCommunity" text class="mobile-actions-item" @click="mobileGoToCommunity" aria-label="Notifications">
                    <icon-with-count-badge :icon="['fas', 'bell']" :count="headerAttentionCount" aria-label="Notifications count" />
                    <span class="label">Notifications</span>
                </primevue-button>
                <primevue-button text class="mobile-actions-item" @click="mobileToggleSettings" aria-label="Settings">
                    <font-awesome-icon :icon="['fad', 'gears']" />
                    <span class="label">Settings</span>
                </primevue-button>

                <primevue-button v-if="authState.loggedIn" text class="mobile-actions-item" @click="mobileToggleFeedback" aria-label="Feedback">
                    <font-awesome-icon :icon="['fas', 'comment-dots']" />
                    <span class="label">Feedback</span>
                </primevue-button>

                <primevue-button v-if="route.meta.videoLink && authState.loggedIn" text class="mobile-actions-item" @click="mobileToggleTutorial" aria-label="Tutorial">
                    <font-awesome-icon :icon="['fas', 'graduation-cap']" />
                    <span class="label">Tutorial</span>
                </primevue-button>
                <div class="mobile-actions-separator" />
                <template v-if="authState.loggedIn">
                    <primevue-button v-if="hasAdminSectionAccess" text class="mobile-actions-item" @click="mobileGoToAdmin" aria-label="Admin">
                        <font-awesome-icon :icon="['fas', 'user-secret']" />
                        <span class="label">Admin</span>
                    </primevue-button>
                    <primevue-button text class="mobile-actions-item" @click="mobileShowTermsDialog" aria-label="Terms and conditions">
                        <font-awesome-icon :icon="['fas', 'book']" />
                        <span class="label">Terms &amp; Conditions</span>
                    </primevue-button>
                    <primevue-button text class="mobile-actions-item" @click="mobileShowApiAndDpsInstructions" aria-label="API and DPS instructions">
                        <font-awesome-icon :icon="['fas', 'up-right-from-square']" />
                        <span class="label">API &amp; DPS instructions</span>
                    </primevue-button>
                    <primevue-button text class="mobile-actions-item" @click="mobileLogout" aria-label="Logout">
                        <font-awesome-icon :icon="['fas', 'right-from-bracket']" />
                        <span class="label">Logout</span>
                    </primevue-button>
                </template>
                <template v-else>
                    <primevue-button text class="mobile-actions-item" @click="mobileShowContactForm" aria-label="Contact us">
                        <font-awesome-icon :icon="['fas', 'envelope']" />
                        <span class="label">Contact us</span>
                    </primevue-button>
                    <primevue-button text class="mobile-actions-item" @click="mobileTriggerLogin" aria-label="Login">
                        <font-awesome-icon :icon="['fas', 'fa-right-to-bracket']" />
                        <span class="label">Login</span>
                    </primevue-button>
                </template>
            </div>
        </popover>
        <header-community-attention-popover
            :documents="userCommunityDocuments"
            @new-feed-count-updated="updateNewUserFeedDocumentCount"
            @open-document="openPageCommunityDocumentFromAttention"
            @open-enact-notifications="openEnactNotificationsFromAttention"
            @open-notifications="openPageCommunityModalFromAttention"
        />
        <popover ref="profile">
            <div class="profile-overlay">
                <button type="button" class="profile-button" @click="goToAdminPage" v-if="hasAdminSectionAccess">Admin</button>
                <button type="button" class="profile-button" @click="goToMySubscriptionsPage">My Subscriptions</button>
                <button type="button" class="profile-button" @click="logout">
                    Logout
                </button>
                <button type="button" class="profile-button" @click="updateShowTermsDialog">Terms & Conditions</button>
                <button type="button" class="profile-button" @click="showApiAndDpsInstructions">API & DPS instructions</button>
            </div>
        </popover>
        <site-settings v-model="siteSettingsModalVisible" />
        <central-modal
            header="Feedback"
            v-model:visible="showFeedback"
        >
            <feedback-overlay @close:overlay="toggleFeedbackMenu" />
        </central-modal>
        <central-modal
            header="Tutorial"
            v-model:visible="showTutorial"
        >
            <tutorial-video :video-link="route.meta.videoLink && authState.loggedIn ? route.meta.videoLink : siteTutorial" />
        </central-modal>
        <central-modal
            size="large"
            header="Terms & Conditions"
            v-model:visible="showTermsDialog"
            @hide="resetTermsTab"
        >
            <tabs v-model:value="termsTab">
                <tab-list>
                    <tab value="research">Research</tab>
                    <tab value="enact">Enact</tab>
                </tab-list>
                <tab-panels>
                    <tab-panel value="research">
                        <insights-terms-content />
                    </tab-panel>
                    <tab-panel value="enact">
                        <enact-terms-content />
                    </tab-panel>
                </tab-panels>
            </tabs>
        </central-modal>
        <header-community-modal ref="communityModal" />
        <contact-form
            v-model:visible="showContactForm"
        />
    </div>
</template>

<script setup lang="ts">

import {
    computed, ref,
} from 'vue';
import { RouteRecordNameGeneric, RouteRecordRaw, useRoute } from 'vue-router';
import { logout, authState, login } from '@lcp/http-auth';
import { sectionRoutes } from '@/routes/sectionRoutes/sectionRoutes.ts';
import { router } from '@/router.ts';
import { useUserAccessStore } from '@/stores/userAccessStore.ts';
import ContactForm from '@/components/deltaGpt/ContactForm.vue';
import DeltaLogo from '@/components/layout/DeltaLogo.vue';
import EnactTermsContent from '@/components/powerMarkets/EnactTermsContent.vue';
import FeedbackOverlay from '@/components/powerMarkets/overlays/FeedbackOverlay.vue';
import InsightsTermsContent from '@/components/deltaGpt/InsightsTermsContent.vue';
import SearchModal from '@/components/powerMarkets/input/SearchModal.vue';
import SiteSettings from '@/components/siteSettings/SiteSettings.vue';
import SparkLogo from '@/components/layout/SparkLogo.vue';
import TutorialVideo from '@/components/common/TutorialVideo.vue';
import HeaderCommunityModal from '@/components/community/HeaderCommunityModal.vue';
import HeaderCommunityAttentionPopover from '@/components/community/HeaderCommunityAttentionPopover.vue';
import IconWithCountBadge from '@/components/common/IconWithCountBadge.vue';
import Popover from 'primevue/popover';
import PrimevueButton from 'primevue/button';
import PrimevueTag from 'primevue/tag';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import { useUIStore } from '@/stores/uiStore.ts';
import { SectionRouteNames } from '@/routes/sectionRoutes/sectionRouteNames.ts';
import CentralModal from '@/components/common/CentralModal.vue';
import { storeToRefs } from 'pinia';
import { useDeltaGptStore } from '@/stores/deltaGpt/deltaGptStore.ts';
import { IDocumentSimple } from '@/interfaces/deltaGpt/IDocument.ts';
import { useEnactNotifications } from '@/composables/powerMarkets/useEnactNotifications.ts';
import { useCommunityFeed } from '@/composables/community/useCommunityFeed.ts';

const siteTutorial = 'https://player.vimeo.com/video/1166369610';
interface SectionRouteInfo {
    img: string;
    info: string;
}

const sectionRouteInformation: Record<SectionRouteNames, SectionRouteInfo> = {
    [SectionRouteNames.powerAndStorage]: {
        img: '/images/home/windmills.png',
        info: 'Benchmarking, live market data and long term forecasting to invest in and navigate the European power sector.',
    },
    [SectionRouteNames.residential]: {
        img: '/images/home/houses.png',
        info: 'Residential energy insights, supporting strategic decisions on technology market growth, consumer behaviour and home optimisation.',
    },
    [SectionRouteNames.commercialAndIndustrial]: {
        img: '/images/home/industry.png',
        info: 'Insights on commercial and industrial energy, helping clients navigate technologies, market trends and decarbonisation pathways.',
    },
    [SectionRouteNames.networks]: {
        img: '/images/home/powerlines.png',
        info: 'Insights for electricity and gas networks, supporting strategic decisions on investment, regulation and energy transition.',
    },
    [SectionRouteNames.evs]: {
        img: '/images/home/cars.png',
        info: 'Electric vehicle charging data, including station locations and usage patterns.',
    },
    [SectionRouteNames.flexibility]: {
        img: '/images/home/flexibility.png',
        info: 'Insights on flexibility at system and household level, supporting strategic decisions across power and energy markets.',
    },
};

const uiStore = useUIStore();
const { isMobileView, siteSettingsModalVisible } = storeToRefs(uiStore);
const route = useRoute();
const userAccessStore = useUserAccessStore();
const deltaGptStore = useDeltaGptStore();
const { sortCommunityDocuments } = useCommunityFeed();
const {
    unseenNotifications,
} = useEnactNotifications();
const profile = ref<InstanceType<typeof Popover> | null>(null);
const mobileActions = ref<InstanceType<typeof Popover> | null>(null);
const communityModal = ref<InstanceType<typeof HeaderCommunityModal> | null>(null);

const showTutorial = ref<boolean>(false);
const showFeedback = ref<boolean>(false);
const showPageLinks = ref<boolean>(false);
const showTermsDialog = ref<boolean>(false);
const termsTab = ref<'research' | 'enact'>('research');
const showContactForm = ref<boolean>(false);
const hoveredRoute = ref<RouteRecordRaw | null>(null);
const hoveredDropdown = ref<RouteRecordRaw | null>(null);
const currentRouteHasSidebar = computed<boolean>(() => !!route.matched.find(r => r.meta.hasSidebar));
const isProd = computed<boolean>(() => import.meta.env.MODE === 'production');
const canViewCommunity = computed<boolean>(() => authState.value.loggedIn);

const currentSection = computed<RouteRecordRaw | null>(() => route.matched[0]);
const isCurrentRouteASection = computed<boolean>(() => route.matched[0] && Object.values(sectionRoutes).some(section => section.name === route.matched[0].name));
const userCommunityDocuments = computed<Array<IDocumentSimple>>(() => deltaGptStore.allDocumentsSimple
    .filter(document => document.isRelevantToUser)
    .sort(sortCommunityDocuments));
const newUserFeedDocumentCount = ref<number>(0);
const headerAttentionCount = computed<number>(() => newUserFeedDocumentCount.value + unseenNotifications.value);

const hasAdminSectionAccess = computed<boolean>(() => (userAccessStore.enactPageAccess.admin.hasAccess || (userAccessStore.flexIndexAccess?.isAdmin ?? false)));

function getDropdownChildren (section: RouteRecordRaw): RouteRecordRaw[] {
    if (!section.children) return [];
    return Object.values(section.children);
}

function openSideNav (): void {
    uiStore.updateShowNavSidebar(!uiStore.showNavSidebar);
    if (uiStore.showNavSidebar) {
        showPageLinks.value = false;
    }
}

function routeHoverEnter (section: RouteRecordRaw): void {
    hoveredRoute.value = section;
}

function routeHoverLeave (): void {
    hoveredRoute.value = null;
}

function dropdownHoverEnter (section: RouteRecordRaw): void {
    hoveredDropdown.value = section;
}

function dropdownHoverLeave (): void {
    hoveredDropdown.value = null;
}

function pageNameNavigation (routeName: RouteRecordNameGeneric): void {
    let routeToPush = routeName;
    if (routeName === `${SectionRouteNames.powerAndStorage}Enact`) {
        if (userAccessStore.userAccess.enact.shortTermMarkets !== null && userAccessStore.userAccess.enact.shortTermMarkets.indexCountryAccess.length) routeToPush = 'benchmarks';
        else if (userAccessStore.userAccess.enact.shortTermMarkets !== null) routeToPush = 'markets';
        else if (userAccessStore.userAccess.enact.longTermMarkets !== null) routeToPush = 'forecasts';
        else if (userAccessStore.userAccess.enact.assetTracking !== null) routeToPush = 'assets';
        else routeToPush = 'markets';
    }
    showPageLinks.value = false;
    hoveredRoute.value = null;
    router.push({ name: routeToPush });
}

function checkLinkSelected (section: RouteRecordRaw): boolean {
    const currentRoute = router.currentRoute.value;
    if (section.name && currentRoute.matched.some(matchedRoute => matchedRoute.name === section.name)) {
        return true;
    }
    return currentRoute.name === section.name;
}

function triggerLogin (): void {
    login();
}

function setShowContactForm (value: boolean): void {
    showContactForm.value = value;
}

function toggleTutorialMenu (): void {
    showTutorial.value = !showTutorial.value;
}

function toggleFeedbackMenu (): void {
    showFeedback.value = !showFeedback.value;
}

function toggleSettingsDrawer (): void {
    uiStore.toggleSiteSettings();
}

function toggleProfile (e: Event): void {
    if (!profile.value) return;
    profile.value.toggle(e);
}

function toggleMobileActions (e: Event): void {
    if (!mobileActions.value) return;
    mobileActions.value.toggle(e);
}

function hideMobileActions (): void {
    mobileActions.value?.hide();
}

function mobileToggleSettings (): void {
    toggleSettingsDrawer();
    hideMobileActions();
}

function mobileToggleFeedback (): void {
    toggleFeedbackMenu();
    hideMobileActions();
}

function mobileToggleTutorial (): void {
    toggleTutorialMenu();
    hideMobileActions();
}

function mobileGoToAdmin (): void {
    goToAdminPage();
    hideMobileActions();
}

function mobileGoToCommunity (): void {
    openPageCommunityModal();
    hideMobileActions();
}

function mobileLogout (): void {
    logout();
    hideMobileActions();
}

function mobileShowTermsDialog (): void {
    updateShowTermsDialog();
    hideMobileActions();
}

function mobileShowApiAndDpsInstructions (): void {
    showApiAndDpsInstructions();
    hideMobileActions();
}

function mobileShowContactForm (): void {
    setShowContactForm(true);
    hideMobileActions();
}

function mobileTriggerLogin (): void {
    triggerLogin();
    hideMobileActions();
}

function updateShowTermsDialog (): void {
    showTermsDialog.value = true;
}

function showApiAndDpsInstructions (): void {
    window.open('https://api.lcpdelta.com/', '_blank');
}

function goToAdminPage (): void {
    router.push({ name: 'admin' });
}

async function fetchCommunityThreadsIfNeeded (): Promise<void> {
    if (!canViewCommunity.value || deltaGptStore.allDocumentsSimple.length > 0) return;

    await deltaGptStore.fetchAllDocumentInfo();
}

async function openPageCommunityModal (): Promise<void> {
    if (!canViewCommunity.value) return;
    await communityModal.value?.open();
}

async function openPageCommunityDocumentFromAttention (document: IDocumentSimple): Promise<void> {
    await communityModal.value?.openDocumentFromAttention(document);
}

async function openEnactNotificationsFromAttention (): Promise<void> {
    await communityModal.value?.openEnactNotifications();
}

function openPageCommunityModalFromAttention (): void {
    openPageCommunityModal();
}

function updateNewUserFeedDocumentCount (count: number): void {
    newUserFeedDocumentCount.value = count;
}

function goToMySubscriptionsPage (): void {
    router.push({ name: 'mySubscriptions' });
}

function resetTermsTab (): void {
    termsTab.value = 'research';
}

fetchCommunityThreadsIfNeeded().catch(() => {
    // The modal fetch path shows the toast if the user explicitly opens Community.
});

</script>

<style lang="scss" scoped>

.navbar-container {
    background-color: var(--neutral0);
    z-index: 100;
    width: 100%;
    border-bottom: 1px solid var(--neutral300);
    height: var(--headerbarHeight);
}

.navbar-content {
    position: relative;
    align-items: center;
    display: flex;
    justify-content: space-between;
    padding: 0 var(--defaultSpacing);
    height: 100%;
}

.navbar-icon-button {
    height: var(--headerbarHeight);
    display: flex;
    flex-direction: column;
    gap: var(--defaultSpacing);
    color: var(--primaryFont);
    font-size: var(--defaultFontSize);
    font-weight: var(--defaultFontWeight);
    background-color: transparent;
    position: relative;
    border-radius: 0;
    padding: var(--smallSpacing) 2.5rem;
    border: transparent;
    transition: color 0.3s ease-in-out;

    @include responsive (sm) {
        height: auto;
    }

    &:hover, &.hovered {
        color: var(--accentColour);
        border-top-right-radius: var(--borderRadiusOuter);
        border-top-left-radius: var(--borderRadiusOuter);
    }

    &.radius {
        height: auto;
        border-radius: var(--borderRadiusOuter);
    }

    &.selected {
        cursor: default;
        text-decoration: underline;
        text-decoration-color: var(--accentColour);
        text-underline-offset: var(--smallSpacing);
        text-decoration-thickness: 2px;

        &:hover, &.hovered {
            background-color: transparent;
            color: var(--primaryFont);
        }
    }

    .dropdown-icon {
        position: absolute;
        bottom: 0.8rem;
        left: 50%;
        color: var(--primaryFont);
        font-size: var(--extraSmallFontSize);
        transform: translateX(-50%);
    }
}

.navbar-dropdown-button-wrapper {
    position: relative;
}

.navbar-dropdown-routes {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: var(--defaultSpacing);
    padding: var(--largeSpacing) var(--largeSpacing) var(--largeSpacing) 0;
}

.navbar-dropdown-wrapper {
    display: grid;
    grid-template-columns: auto auto;
    gap: var(--largeSpacing);
    background-color: var(--neutral0);
    position: fixed;
    top: var(--headerbarHeight);
    left: 50%;
    transform: translateX(-50%);
    border-bottom-right-radius: var(--borderRadiusOuter);
    border-bottom-left-radius: var(--borderRadiusOuter);
    overflow: hidden;
    border: 1px solid var(--neutral300);
    align-items: start;
    padding: var(--largeSpacing);
    min-width: 100%;
    padding: 0 var(--largeSpacing);

    .navbar-dropdown-content {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: var(--largeSpacing);
        padding: var(--largeSpacing);
        overflow: hidden;
        isolation: isolate;

        @include responsive(sm) {
            padding: var(--defaultSpacing);
        }

        &::after {
            content: '';
            position: absolute;
            z-index: 1;
            background: linear-gradient(to bottom, var(--neutral0) 0, transparent 20%, var(--neutral0) 80%, var(--neutral0) 100%);
            height: 100%;
            width: 100%;
            inset: 0;
            pointer-events: none;
            padding: var(--largeSpacing);
        }

        .navbar-dropdown-image {
            width: 100%;

            img {
                display: block;
                width: 100%;
                aspect-ratio: 16 / 9;
                object-fit: cover;
                object-position: 0% 0%;
                opacity: 0.8;
            }
        }

        .navbar-dropdown-text {
            position: relative;
            z-index: 2;
            padding: var(--largeSpacing) 0;
            font-size: var(--smallFontSize);
            color: var(--neutral900);

            @include responsive(sm) {
                padding: 0;
            }
        }
    }

    .navbar-dropdown-button {
        padding: var(--defaultSpacing) var(--extraLargeSpacing);
        transition: all 0.3s ease-in-out;
        color: var(--primaryFont);
        background-color: transparent;
        border-radius: 0;
        justify-content: left;

        &.has-grandchildren {
            padding-bottom: 0;
        }

        &.grandchild-button {
            padding: 0 var(--extraLargeSpacing);
            color: var(--neutral800);
        }

        .text {
            position: relative;
            transition: all 0.3s ease-in-out;
            transform: translateX(0);
            white-space: nowrap;

            .dropdown-enact-brand {
                position: relative;

                .dropdown-enact-bulb {
                    position: absolute;
                    top: 50%;
                    right: calc(100% + 0.2rem);
                    transform: translateY(-50%);
                    height: 1.5rem;
                }
            }
        }

        .dropdown-icon {
            position: absolute;
            right: calc(-1 * var(--extraLargeSpacing));
            top: 50%;
            transform: translateY(-50%);
            font-size: var(--extraSmallFontSize);
        }

        &.grandchild-button {
            padding: 0 var(--extraLargeSpacing);
        }

         &:hover {
            color: var(--accentColour);

            .text {
                transform: translateX(calc(-1 * var(--smallSpacing)));
            }
        }
    }
}

.navbar-dropdown-button {
    text-align: left;

    &.has-grandchildren {
        padding-bottom: 0;
    }

    &.grandchild-button {
        color: var(--neutral800);
    }
}

.navbar-routes-button-holder {
    gap: var(--largeSpacing);
    font-weight: var(--boldFontWeight);
    border: solid 1px var(--neutral500);
    color: var(--lcpBlueHoverBright);
    border-radius: var(--borderRadiusOuter);
    padding: 0 var(--defaultSpacing);
    margin: var(--extraSmallSpacing) 0;
    user-select: none;
    position: relative;
    white-space: nowrap;

    @include responsive(sm) {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .navbar-routes-button-header {
        position: absolute;
        top: -1.2rem;
        right: 1rem;
        background: var(--neutral0);
        padding: 0 var(--extraSmallSpacing);
        font-size: var(--smallFontSize);

        &.enact-brand {
            top: -1.7rem;
        }

        .enact-logo {
            width: 6.5rem;
        }

        sup {
            font-size: var(--extraSmallFontSize);
        }

        @include responsive('md') {
            background: var(--neutral100);
        }
    }

    .navbar-routes-button {
        padding: 0.4rem var(--defaultSpacing);
    }
}

.navbar-routes-button {
    padding: var(--smallSpacing) var(--defaultSpacing);
    color: var(--primaryFont);
    font-size: var(--defaultFontSize);
    font-weight: var(--defaultFontWeight);
    background-color: transparent;
    text-align: left;
    border-radius: 0;

    &:hover {
        color: var(--accentColour);
    }
    &.selected {
        cursor: default;
        text-decoration: underline;
        text-decoration-color: var(--accentColour);
        text-underline-offset: var(--smallSpacing);
        text-decoration-thickness: 2px;

        &:hover {
            background-color: transparent;
            color: var(--primaryFont);
        }
    }
}

.logo-wrapper {
    display: flex;
    align-items: center;
    gap: var(--defaultSpacing);

    .logo-content {
        display: flex;
        gap: var(--defaultSpacing);
        padding-left: var(--smallSpacing);
        cursor: pointer;
        background: none;
        border: none;
    }

    .delta-logo {
        height: 2rem;
    }

    .logo {
        display: flex;
        gap: var(--smallSpacing);
        align-items: center;
        color: var(--primaryFont);

        &.spark {
            height: 2.5rem;
            cursor: pointer;
        }
    }
    .p-tag {
        font-size: var(--extraSmallFontSize);
        user-select: none;
    }
}

.navbar-end {
    display: flex;
    gap: var(--largeSpacing);
    align-items: center;
    justify-content: flex-end;

    .navbar-icon {
        color: var(--primaryFont);
    }

    .feature-button {
        overflow: unset !important;
        position: relative;
        display: inline-flex;

    }

    .settings-button {
        padding: 0;
        font-size: var(--extraLargeFontSize);
    }
    .feedback-button {
        font-size: var(--extraLargeFontSize);
    }
    .tutorial-button {
        padding: var(--smallSpacing);
        font-size: var(--extraLargeFontSize);
    }
    .logged-out-buttons {
        margin-right: var(--smallSpacing);
        display: flex;
        gap: var(--largeSpacing);
        .login-btn {
            gap: var(--largeSpacing);
        }
    }
}

.video-holder {
    position: relative;
    iframe {
        height: 720px;
        width: 1280px;
        @include responsive('xl') {
            height: 430px;
            width: 764px;
        }
    }
}

.profile-overlay {
    display: flex;
    flex-direction: column;
    .profile-button {
        color: var(--primaryFont);
        padding: var(--largeSpacing) var(--defaultSpacing);
        &:hover {
            background-color: var(--neutral0);
        }
    }
}

#header-navbar-mobile-btn {
    display: none;

    @include responsive('md') {
        display: flex;
        justify-content: center;
        align-items: center;
    }
}

.header-navbar-center {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    margin: 0 var(--largeSpacing);

    @include responsive('md') {
        position: none;
        left: unset;
        // inset: 0;
        transform: none;
        margin: 0;
    }
}

.header-navbar-center-content {
    display: flex;
    justify-content: center;
    align-items: center;

    @include responsive('md') {
        border: 1px solid var(--neutral300);
        background-color: var(--neutral100);
        flex-direction: column;
        gap: var(--defaultSpacing);
        position: fixed;
        padding: var(--defaultSpacing);
        top: var(--headerbarHeight);
        right: var(--defaultSpacing);
        left: var(--defaultSpacing);
        display: none;

        &.show-page-links {
            display: flex;
        }
    }

    .accordion-content-wrapper {
        display: flex;
    }
}

.stacked-routes-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--extraSmallSpacing);
    align-items: center;
    justify-content: center;
    height: 100%;
    padding-top: var(--smallSpacing);
}

.navbar-routes-wrapper {
    display: flex;
    justify-content: center;

    .navbar-routes-content {
        display: flex;
        gap: 2rem;

        @include responsive('md') {
            gap: var(--largeSpacing);
            flex-direction: column;
        }
    }
}

.breadcrumb-container {
    display: flex;
    align-items: center;
    gap: var(--smallSpacing);
    background-color: var(--neutral200);
    border-radius: var(--borderRadiusOuter);

    .breadcrumb-button {
        background-color: transparent;
        color: var(--primaryFont);
        font-size: var(--defaultFontSize);
        border: none;
        padding: var(--smallSpacing) var(--defaultSpacing);

        &:hover {
            color: var(--accentColour);
        }
    }

    .breadcrumb-separator {
        font-size: var(--smallFontSize);
        user-select: none;
        cursor: default;
    }

    .breadcrumb-selected {
        cursor: default;
        font-size: var(--smallFontSize);
        padding: var(--smallSpacing) var(--defaultSpacing);
        text-decoration: underline;
        text-decoration-color: var(--accentColour);
        text-decoration-thickness: 1px;
        text-underline-offset: 3px;
    }
}

.mobile-menu-btn {
    display: none;
    height: 30px;
    justify-self: flex-end;
    width: 30px;
    @include responsive('md') {
        display: block;
    }
}
.disabled-icon {
    top: 0;
    right: 0;
    position: absolute;
}
.no-wrap {
    white-space: nowrap;
}

.mobile-actions-btn {
    display: none;
    color: var(--primaryFont);

    @include responsive('md') {
        display: block;
    }
}

.mobile-actions-overlay {
    display: flex;
    flex-direction: column;
    min-width: 220px;
}

.mobile-actions-item {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: var(--defaultSpacing);
    width: 100%;

    &.search {
        justify-content: flex-end;
    }
}

.mobile-actions-separator {
    height: 1px;
    width: 100%;
    background: var(--neutral300);
    margin: var(--smallSpacing) 0;
}

.label {
    white-space: nowrap;
    color: var(--primaryFont);
}

.mobile-nav-burger {
    display: none;
    color: var(--primaryFont);

    @include responsive('md') {
        display: block;
    }
}
</style>
